import React, { Suspense } from 'react';
import { Route, Routes as RouterRoutes, Navigate, useLocation } from 'react-router-dom';
import DashboardLayout from 'layouts/Dashboard';
import Page404 from '../pages/auth/Page404';
import { RouteType } from 'types/routes';
import { HOME_PATH } from '../constants/paths';
import { DynamicModalProvider } from 'components/DynamicModalProvider/DynamicModalProvider';
import { useAppContext } from 'containers/AppProvider';
import { Box, CircularProgress } from '@mui/material';
import useUrlTeamNavigation from 'hooks/useUrlTeamNavigation';

// Loading fallback component for lazy-loaded routes
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
    }}
  >
    <CircularProgress />
  </Box>
);

// Backward compatibility redirect components
const RedirectTeamsToReports = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  // Special handling for team profile deep links with URL parameters
  // Calls useUrlTeamNavigation to handle the redirect to team profile
  useUrlTeamNavigation();

  const hasTeamId = queryParams.has('team_id');
  const hasRealmId = queryParams.has('realm_id');
  const hasTenantName = queryParams.has('tenant_name');

  if (hasTeamId && hasRealmId && hasTenantName) {
    // Let useUrlTeamNavigation handle the redirect
    // Return loading state while redirect happens
    return <LoadingFallback />;
  }

  // Normal redirect for backward compatibility
  const newPath = location.pathname.replace(/^\/teams/, '/reports');
  return <Navigate to={`${newPath}${location.search}${location.hash}`} replace />;
};

const RedirectTeamsV2ToTeams2 = () => {
  const location = useLocation();
  const newPath = location.pathname.replace(/^\/teamsV2/, '/teams-2');
  return <Navigate to={`${newPath}${location.search}${location.hash}`} replace />;
};

const childRoutes = (Layout: React.ElementType, routes: Array<RouteType>) =>
  routes.map(({ component: Component, guard, children, path }, index: number) => {
    const Guard = guard || React.Fragment;

    return children ? (
      children.map((element, childIndex: number) => {
        const Guard = element.guard || React.Fragment;
        const ElementComponent = element.component || React.Fragment;

        return (
          <Route
            key={element.path || `child-${index}-${childIndex}`}
            path={element.path}
            element={
              <Layout>
                <Guard>
                  <Suspense fallback={<LoadingFallback />}>
                    <ElementComponent />
                  </Suspense>
                </Guard>
              </Layout>
            }
          />
        );
      })
    ) : Component ? (
      <Route
        key={path || `route-${index}`}
        path={path}
        element={
          <Layout>
            <Guard>
              <Suspense fallback={<LoadingFallback />}>
                <Component />
              </Suspense>
            </Guard>
          </Layout>
        }
      />
    ) : null;
  });

const Routes = () => {
  const { routes } = useAppContext();

  return (
    <DynamicModalProvider>
      <RouterRoutes>
        {childRoutes(DashboardLayout, routes)}
        {/* <Route
        exact
        path={REPORTS_TABLE_TEAMS_PATH_DEEP_LINK}
        render={(params) => {
          // eslint-disable-next-line no-console
          console.info("MATCH?", params);

          return <Redirect to={getReportTeamsPageDeepLinkUrl({})} />;
        }}
      /> */}
        {/* {childRoutes(AuthLayout, authLayoutRoutes)} */}{' '}
        <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
        {/* Backward compatibility redirects for external deep links */}
        <Route path="/teams/*" element={<RedirectTeamsToReports />} />
        <Route path="/teams" element={<RedirectTeamsToReports />} />
        <Route path="/teamsV2/*" element={<RedirectTeamsV2ToTeams2 />} />
        <Route path="/teamsV2" element={<RedirectTeamsV2ToTeams2 />} />
        {/*
          Show 404 page for unknown URLs.
          Uses DashboardLayout so logged-in users see sidebar navigation.
          Unauthenticated users will be redirected to login by the auth layer
          before reaching this route.
        */}
        <Route
          path="*"
          element={
            <DashboardLayout>
              <Page404 />
            </DashboardLayout>
          }
        />
      </RouterRoutes>
    </DynamicModalProvider>
  );
};

export default Routes;
